import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { getAllStudents } from '../services/userService';
import Toast from './Toast';
import {
  getSightWords,    getAssignedSightWordIds,    assignSightWordToStudent,    unassignSightWordFromStudent,
  getScrambleWords, getAssignedScrambleWordIds, assignScrambleWordToStudent, unassignScrambleWordFromStudent,
  getCategoryWords, getAssignedCategoryWordIds, assignCategoryWordToStudent, unassignCategoryWordFromStudent,
} from '../services/contentService';

const TABS = [
  { key: 'sight',    label: 'Sight Words' },
  { key: 'scramble', label: 'Scramble Words' },
  { key: 'category', label: 'Category Words' },
];

const tabStyle = (active) => ({
  padding: '8px 16px',
  backgroundColor: active ? '#4ECDC4' : '#eee',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: active ? 'bold' : 'normal',
});

const StudentWordAssignment = () => {
  const { currentUser } = useUser();

  const [students, setStudents]           = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [activeTab, setActiveTab]         = useState('sight');

  const [allWords, setAllWords]           = useState({ sight: [], scramble: [], category: [] });
  const [assignedIds, setAssignedIds]     = useState({ sight: new Set(), scramble: new Set(), category: new Set() });
  const [loadingWords, setLoadingWords]   = useState(false);
  const [toggling, setToggling]           = useState(null); // word id being toggled
    const [toast, setToast]                = useState(null);

  // Load all students on mount
  useEffect(() => {
    getAllStudents().then(({ data }) => setStudents(data || []));
  }, []);

  // Load all word lists on mount
  useEffect(() => {
    Promise.all([getSightWords(), getScrambleWords(), getCategoryWords()]).then(
      ([sight, scramble, category]) => {
        setAllWords({
          sight:    sight.data    || [],
          scramble: scramble.data || [],
          category: category.data || [],
        });
      }
    );
  }, []);

  // Load assigned word IDs whenever student or tab changes
  const loadAssignedIds = useCallback(async () => {
    if (!selectedStudent) return;
    setLoadingWords(true);

    const [sightIds, scrambleIds, categoryIds] = await Promise.all([
      getAssignedSightWordIds(selectedStudent),
      getAssignedScrambleWordIds(selectedStudent),
      getAssignedCategoryWordIds(selectedStudent),
    ]);

    setAssignedIds({
      sight:    new Set(sightIds.data    || []),
      scramble: new Set(scrambleIds.data || []),
      category: new Set(categoryIds.data || []),
    });
    setLoadingWords(false);
  }, [selectedStudent]);

  useEffect(() => { loadAssignedIds(); }, [loadAssignedIds]);

  const handleToggle = async (wordId, currentlyAssigned) => {
    if (!selectedStudent || toggling) return;
    setToggling(wordId);

    const assignFns = {
      sight:    { assign: assignSightWordToStudent,    unassign: unassignSightWordFromStudent },
      scramble: { assign: assignScrambleWordToStudent, unassign: unassignScrambleWordFromStudent },
      category: { assign: assignCategoryWordToStudent, unassign: unassignCategoryWordFromStudent },
    };

    const fn = currentlyAssigned
      ? assignFns[activeTab].unassign
      : assignFns[activeTab].assign;

    const params = currentlyAssigned
      ? { studentId: selectedStudent, wordId }
      : { studentId: selectedStudent, wordId, assignedBy: currentUser?.id };

    // Optimistic update
    setAssignedIds((prev) => {
      const next = new Set(prev[activeTab]);
      currentlyAssigned ? next.delete(wordId) : next.add(wordId);
      return { ...prev, [activeTab]: next };
    });

    const { error } = await fn(params);
    if (error) {
      // Revert on failure
      setAssignedIds((prev) => {
        const next = new Set(prev[activeTab]);
        currentlyAssigned ? next.add(wordId) : next.delete(wordId);
        return { ...prev, [activeTab]: next };
      });
              setToast({ message: error.message || 'Failed to update assignment.', type: 'error' });
    }

    setToggling(null);
  };

  const currentWords    = allWords[activeTab] || [];
  const currentAssigned = assignedIds[activeTab] || new Set();
  const selectedStudentProfile = students.find((s) => s.id === selectedStudent);

  return (
    <div>
      <h3>Assign Words to Students</h3>

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="student-select" style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>
          Select Student:
        </label>
        <select
          id="student-select"
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', minWidth: '220px' }}
        >
          <option value="">-- Choose a student --</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.first_name} {s.last_name} ({s.username})
            </option>
          ))}
        </select>
      </div>

      {!selectedStudent ? (
        <p style={{ color: '#888' }}>Select a student above to manage their word assignments.</p>
      ) : (
        <>
          <p style={{ marginBottom: '1rem' }}>
            Managing words for: <strong>{selectedStudentProfile?.first_name} {selectedStudentProfile?.last_name}</strong>
            &nbsp;({currentAssigned.size} {activeTab} word{currentAssigned.size !== 1 ? 's' : ''} assigned)
          </p>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem' }}>
            {TABS.map(({ key, label }) => (
              <button key={key} onClick={() => setActiveTab(key)} style={tabStyle(activeTab === key)}>
                {label}
              </button>
            ))}
          </div>

          {loadingWords ? (
            <p>Loading...</p>
          ) : currentWords.length === 0 ? (
            <p>No {activeTab} words in the database yet. Add them in the other tabs first.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {currentWords.map((w) => {
                const assigned = currentAssigned.has(w.id);
                return (
                  <div
                    key={w.id}
                    onClick={() => handleToggle(w.id, assigned)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.6rem 1rem',
                      border: `2px solid ${assigned ? '#4ECDC4' : '#ddd'}`,
                      borderRadius: '8px',
                      backgroundColor: assigned ? '#e8fafa' : '#fafafa',
                      cursor: toggling === w.id ? 'wait' : 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={assigned}
                      style={{ width: '18px', height: '18px', accentColor: '#4ECDC4' }}
                    />
                    <span style={{ fontWeight: '600' }}>{w.word}</span>
                    {activeTab === 'scramble' && (
                      <span style={{ color: '#888', fontSize: '0.85rem' }}>({w.scrambled})</span>
                    )}
                    {activeTab === 'category' && (
                      <span style={{ color: '#888', fontSize: '0.85rem' }}>{w.correct_category}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
};

export default StudentWordAssignment;
